import { forwardRef, useEffect, useState } from 'react'
import logo from '@/assets/logos/ava-logo.png'
import { getQrCodeUrl } from '@/lib/qrCode'

interface QRCardProps {
  title: string
  url: string
  subtitle?: string
  onReady?: () => void
}

// Replace regular spaces with non-breaking spaces to prevent html2canvas from collapsing them
const preserveSpaces = (text: string): string => {
  return text.replace(/ /g, '\u00A0')
}

// Convert external image to data URL to avoid CORS issues with html2canvas
const imageToDataUrl = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      } else {
        reject(new Error('Could not get canvas context'))
      }
    }
    img.onerror = reject
    img.src = src
  })
}

const QRCard = forwardRef<HTMLDivElement, QRCardProps>(
  ({ title, url, subtitle, onReady }, ref) => {
    const [qrDataUrl, setQrDataUrl] = useState<string>('')
    const [logoDataUrl, setLogoDataUrl] = useState<string>('')
    const [qrLoaded, setQrLoaded] = useState(false)
    const [logoLoaded, setLogoLoaded] = useState(false)

    // Notify parent when both images are ready
    useEffect(() => {
      if (qrLoaded && logoLoaded && onReady) {
        onReady()
      }
    }, [qrLoaded, logoLoaded, onReady])

    useEffect(() => {
      setQrLoaded(false)
      setLogoLoaded(false)

      // Convert QR code to data URL
      const qrCodeUrl = getQrCodeUrl(url)
      imageToDataUrl(qrCodeUrl)
        .then((dataUrl) => {
          setQrDataUrl(dataUrl)
          setQrLoaded(true)
        })
        .catch(() => {
          setQrDataUrl(qrCodeUrl) // Fallback to original URL
          setQrLoaded(true)
        })

      // Convert logo to data URL
      imageToDataUrl(logo)
        .then((dataUrl) => {
          setLogoDataUrl(dataUrl)
          setLogoLoaded(true)
        })
        .catch(() => {
          setLogoDataUrl(logo)
          setLogoLoaded(true)
        })
    }, [url])

    return (
      <div
        ref={ref}
        className="w-[320px] bg-white rounded-lg shadow-lg overflow-hidden"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        {/* Header */}
        <div style={{ backgroundColor: '#1a1a2e', padding: '16px 24px' }}>
          <h2 style={{
            color: 'white',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 500,
            margin: 0,
          }}>
            {preserveSpaces(title)}
          </h2>
        </div>

        {/* QR Code */}
        <div style={{ padding: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR Code"
              style={{ width: '200px', height: '200px' }}
            />
          ) : (
            <div style={{ width: '200px', height: '200px', backgroundColor: '#f0f0f0' }} />
          )}

          {subtitle && (
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <p style={{
                fontSize: '18px',
                fontWeight: 500,
                color: '#111827',
                margin: 0,
              }}>
                {preserveSpaces(subtitle)}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {/* <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f3f4f6',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          <span style={{
            fontSize: '14px',
            color: '#6b7280',
            lineHeight: '20px',
          }}>
            {preserveSpaces('Powered by')}
          </span>
          {logoDataUrl && (
            <img
              src={logoDataUrl}
              style={{ height: '20px', verticalAlign: 'middle' }}
            />
          )}
        </div> */}
      </div>
    )
  }
)

QRCard.displayName = 'QRCard'

export default QRCard
